import {
  Link,
  mergeStyleSets,
  MessageBar,
  MessageBarButton,
  MessageBarType,
} from "@fluentui/react";
import ConfigPipelineDialog from "../../Experiment/RolloutDetails/RolloutDetailsTable/ConfigurePipeline/ConfigPipelineDialog";
import { useBoolean } from "@fluentui/react-hooks";
import { useFetchPipelineConfigOpenPrs } from "../../Hooks/AdoHooks/UseFetchExperiments";
import { useAppContext } from "../../Contexts";
import { NavType } from "../../Shared/AppInsights";
import useAppInsights from "../../Hooks/UseAppInsights";

/**
 * Banner component to notify users about missing pipeline configuration.
 * Displays a warning message and provides button to configure pipelines.
 */
const ExperimentsTableConfigPipelineBanner: React.FC = () => {
  const { setError } = useAppContext();
  const { trackNavigationEvent, trackOperationEvent } = useAppInsights(
    "ExperimentsTableConfigPipelineBanner",
  );

  const [
    isConfigDialogOpen,
    { setTrue: openConfigDialog, setFalse: closeConfigDialog },
  ] = useBoolean(false);

  const { value: configPr, refresh } = useFetchPipelineConfigOpenPrs(setError);

  const onConfigureClick = () => {
    openConfigDialog();
    trackOperationEvent({
      operation: "OpenConfigurePipelineDialog",
      function: "onConfigureClick",
    });
  };

  const onSuccess = () => {
    refresh();
    closeConfigDialog();
  };

  const onCancelConfigDialog = () => {
    closeConfigDialog();
  };

  const hasOpenPr = !!configPr && !!configPr.prUrl;

  return (
    <>
      <MessageBar
        messageBarType={MessageBarType.warning}
        isMultiline={false}
        dismissButtonAriaLabel="Close"
        className={styles.messageBar}
        actions={
          <div>
            <MessageBarButton onClick={onConfigureClick}>
              {hasOpenPr ? "Update" : "Configure"}
            </MessageBarButton>
          </div>
        }
      >
        {hasOpenPr ? (
          <>
            Pipeline configuration update is pending in an open PR. Please{""}
            <Link
              href={configPr.prUrl ?? ""}
              target="_blank"
              onClick={() => {
                trackNavigationEvent({
                  destination: configPr.prUrl!,
                  navType: NavType.External,
                });
              }}
            >
              review the PR
            </Link>{" "}
            to complete the update or click Update to modify the configuration.
          </>
        ) : (
          "Pipeline IDs not configured for this extension. Rollout data will not be displayed until pipeline IDs are configured. Please click Configure to set up the pipelines."
        )}
      </MessageBar>
      {isConfigDialogOpen && (
        <ConfigPipelineDialog
          onCancel={onCancelConfigDialog}
          onSuccess={onSuccess}
          configuration={configPr}
        />
      )}
    </>
  );
};

const styles = mergeStyleSets({
  messageBar: {
    "& .ms-Button--default": {
      minHeight: "auto",
    },
  },
});

export default ExperimentsTableConfigPipelineBanner;
